import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query';
import type { ExportTransactionsRequest, SdExportKind } from '@erp/rpc';
import { useGT } from 'gt-react';
import { orpc, rpcClient } from '@/api/rpc/client';
import { startRpcDownloadInFrame } from '@/api/rpc/downloadUrl';
import { useToast } from '@/context/ToastContext';
import { triggerExcelDownload } from '@/utils/excelDownload';
import { resolveExportNarrowing, type ExportScope } from './exportSelection';

// Re-exported for the many S&D callers that import it from here.
export { triggerExcelDownload };

/** Dynamic column set — only `id` is guaranteed; all other fields are snake_case from the pipeline. */
export type Transaction = { id: string } & Record<string, unknown>;

/**
 * Valid transaction status values (mirrors the backend SdE844dStatusSchema).
 * "posted" is NOT a status — it's an orthogonal fact carried by the `posted_at`
 * timestamp (a posted row stays `staged`). Use `isPosted` for that fact, and
 * `isPostedFrozen` to decide whether the posting freezes the row.
 */
export type SdTransactionStatus =
  | 'pending'
  | 'staged'
  | 'staged_internal'
  | 'processing'
  | 'sent'
  | 'resubmission_pending'
  | 'no_claim'
  | 'deleted';

export const SD_TRANSACTION_STATUS = {
  PENDING: 'pending',
  STAGED: 'staged',
  STAGED_INTERNAL: 'staged_internal',
  PROCESSING: 'processing',
  SENT: 'sent',
  RESUBMISSION_PENDING: 'resubmission_pending',
  NO_CLAIM: 'no_claim',
} as const satisfies Record<string, SdTransactionStatus>;

/**
 * Has this transaction been GL-posted? A plain boolean FACT, carried by the
 * `posted_at` timestamp — posting is not a lifecycle stage, so it does not live
 * in `status` (see migration 20260701180040-add-sd-e844d-posted-at).
 *
 * The fact is permanent: `posted_at` is retained through processing → sent, and
 * a cancelled submission (`markAsFailed`) moves rows `sent` →
 * `resubmission_pending` without clearing it. So this answers "was it posted",
 * NOT "is it editable" — for that use `isPostedFrozen`.
 */
export function isPosted(transaction: Transaction): boolean {
  return transaction.posted_at != null;
}

/**
 * Is this transaction FROZEN by the posting? The posted fact plus the one
 * lifecycle stage in which it freezes the row — i.e. posted but not yet sent.
 *
 * This is the predicate every edit/unstage/no-claim guard wants, and it must
 * stay identical to the backend, which freezes exactly `status = 'staged' AND
 * posted_at IS NOT NULL` — see `assertTransactionEditable`'s own
 * `isPostedFrozen` (e844sdTransactions.ts) and the model guards
 * `guardPostedImmutability` / `guardPostedBulkImmutability` (SdE844d.model.ts).
 *
 * Guarding on the bare `isPosted` fact instead over-freezes every later stage:
 * it made a cancelled (`resubmission_pending`) row read as frozen, so the UI
 * refused an edit the backend would have accepted and the Save button silently
 * did nothing.
 */
export function isPostedFrozen(transaction: Transaction): boolean {
  return (
    isPosted(transaction) && transaction.status === SD_TRANSACTION_STATUS.STAGED
  );
}

/** Cache data shapes for optimistic updates */
interface TransactionsPageData {
  transactions: Array<{ id: string } & Record<string, unknown>>;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/** Stable table ID used for user/org column config lookups (must match the
 *  tableId passed to `useColumnConfig(...)` on every S&D transaction page). */
export const SD_TABLE_ID = 'ship-and-debit';

export function invalidateE844sdQueries(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.getStagedStats.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.searchAll.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.getActivityLog.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.getStatusCounts.key(),
  });
  void queryClient.invalidateQueries({
    queryKey:
      orpc.shipAndDebit.e844sdTransactions.getClassificationBreakdown.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.getUserActivityStats.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.getClassificationStats.key(),
  });
  void queryClient.invalidateQueries({
    queryKey:
      orpc.shipAndDebit.e844sdTransactions.getSupplierAcceptanceRates.key(),
  });
}

function invalidateE844sdQueriesExceptList(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.getStagedStats.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.searchAll.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.getActivityLog.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.getStatusCounts.key(),
  });
  void queryClient.invalidateQueries({
    queryKey:
      orpc.shipAndDebit.e844sdTransactions.getClassificationBreakdown.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.getUserActivityStats.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.e844sdTransactions.getClassificationStats.key(),
  });
  void queryClient.invalidateQueries({
    queryKey:
      orpc.shipAndDebit.e844sdTransactions.getSupplierAcceptanceRates.key(),
  });
}

function patchTransactionInListCache({
  queryClient,
  transactionId,
  updates,
}: {
  queryClient: QueryClient;
  transactionId: string;
  updates: Record<string, unknown>;
}) {
  queryClient.setQueriesData<TransactionsPageData>(
    { queryKey: orpc.shipAndDebit.e844sdTransactions.list.key() },
    (old) => {
      if (!old) return old;
      return {
        ...old,
        transactions: old.transactions.map((tx) =>
          tx.id === transactionId ? { ...tx, ...updates } : tx,
        ),
      };
    },
  );
}

function removeTransactionsFromListCache(
  queryClient: QueryClient,
  transactionIds: string[],
) {
  const ids = new Set(transactionIds);
  queryClient.setQueriesData<TransactionsPageData>(
    { queryKey: orpc.shipAndDebit.e844sdTransactions.list.key() },
    (old) => {
      if (!old) return old;
      const filtered = old.transactions.filter((tx) => !ids.has(tx.id));
      return {
        ...old,
        transactions: filtered,
        pagination: {
          ...old.pagination,
          total:
            old.pagination.total - (old.transactions.length - filtered.length),
        },
      };
    },
  );
}

/**
 * Unified hook for fetching transactions by status with pagination.
 * Replaces useNoClaimTransactions, useStagedTransactions, and useStagedInternalTransactions.
 */
export function useListTransactions(
  params: {
    statuses: SdTransactionStatus[];
    limit?: number;
    offset?: number;
    columnFilters?: Record<string, string[]>;
    textFilters?: Record<string, string[]>;
    dateFilters?: Record<string, string[]>;
    numericFilters?: Record<string, string[]>;
    hasComments?: boolean;
    dunsNotOnSpa?: boolean;
    search?: string;
    searchColumns?: string[];
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    transactionIds?: string[];
  },
  options: { enabled?: boolean } = {},
) {
  const query = useQuery({
    ...orpc.shipAndDebit.e844sdTransactions.list.queryOptions({
      input: params,
    }),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  });

  return {
    ...query,
    transactions: query.data?.transactions ?? [],
    pagination: query.data?.pagination,
  };
}

/**
 * Unified hook for fetching all transaction IDs matching filters (on-demand mutation).
 * Replaces useGetPendingTransactionIds and useGetNoClaimTransactionIds.
 */
export function useListTransactionIds() {
  return useMutation(
    orpc.shipAndDebit.e844sdTransactions.listIds.mutationOptions({}),
  );
}

/**
 * Hook for marking transactions as no-claim
 */
export function useMarkAsNoClaim() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (transactionIds: string[]) => {
      return rpcClient.shipAndDebit.e844sdTransactions.markAsNoClaim({
        transactionIds,
      });
    },
    onSuccess: (_data, variables) => {
      removeTransactionsFromListCache(queryClient, variables);
      invalidateE844sdQueriesExceptList(queryClient);
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to mark transactions as no-claim',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}

/**
 * Hook for unmarking no-claim transactions (returns them to pending)
 */
export function useUnmarkAsNoClaim() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (transactionIds: string[]) => {
      return rpcClient.shipAndDebit.e844sdTransactions.unmarkAsNoClaim({
        transactionIds,
      });
    },
    onSuccess: (_data, variables) => {
      removeTransactionsFromListCache(queryClient, variables);
      invalidateE844sdQueriesExceptList(queryClient);
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to unmark transactions',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}

/**
 * Hook for staging transactions (pending → staged)
 *
 * Accepts `{ transactionIds, transactions }` — the `transactions` array is
 * frontend-only (not sent to the backend) and used to populate the staged
 * cache optimistically so the UI updates instantly.
 */
export function useStageTransactions() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: {
      transactionIds: string[];
      transactions: Array<{ id: string } & Record<string, unknown>>;
    }) => {
      return rpcClient.shipAndDebit.e844sdTransactions.stage({
        transactionIds: input.transactionIds,
      });
    },

    onMutate: async (variables) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: orpc.shipAndDebit.e844sdTransactions.list.key(),
      });

      // Snapshot list caches for rollback (covers all status variants)
      const previousList = queryClient.getQueriesData<TransactionsPageData>({
        queryKey: orpc.shipAndDebit.e844sdTransactions.list.key(),
      });

      const stagedIds = new Set(variables.transactionIds);

      // Remove staged IDs from list caches (all filter/page/status variants)
      queryClient.setQueriesData<TransactionsPageData>(
        { queryKey: orpc.shipAndDebit.e844sdTransactions.list.key() },
        (old) => {
          if (!old) return old;
          const filtered = old.transactions.filter((t) => !stagedIds.has(t.id));
          return {
            ...old,
            transactions: filtered,
            pagination: {
              ...old.pagination,
              total:
                old.pagination.total -
                (old.transactions.length - filtered.length),
            },
          };
        },
      );

      return { previousList };
    },

    onError: (error: Error, _variables, context) => {
      // Restore snapshots on failure
      if (context?.previousList) {
        for (const [key, data] of context.previousList) {
          queryClient.setQueryData(key, data);
        }
      }

      showToast({
        message: error.message || 'Failed to stage transactions',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },

    onSettled: () => {
      invalidateE844sdQueriesExceptList(queryClient);
    },
  });
}

/**
 * Hook for unstaging transactions (staged → pending)
 *
 * Optimistically removes transactions from the staged cache and updates stats.
 * Does not try to add back to available list (server-side filtering/pagination
 * makes that unreliable — the refetch handles it).
 */
export function useUnstageTransactions() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: string[] | { all: true }) => {
      if (Array.isArray(input)) {
        return rpcClient.shipAndDebit.e844sdTransactions.unstage({
          transactionIds: input,
        });
      }
      return rpcClient.shipAndDebit.e844sdTransactions.unstage({ all: true });
    },

    onMutate: async (input) => {
      const isAll = !Array.isArray(input);

      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: orpc.shipAndDebit.e844sdTransactions.list.key(),
      });

      // Snapshot list caches for rollback (covers all status variants)
      const previousList = queryClient.getQueriesData<TransactionsPageData>({
        queryKey: orpc.shipAndDebit.e844sdTransactions.list.key(),
      });

      if (isAll) {
        // Clear all staged data from cache
        queryClient.setQueriesData<TransactionsPageData>(
          {
            queryKey: orpc.shipAndDebit.e844sdTransactions.list.key(),
          },
          (old) => {
            if (!old) return old;
            return {
              ...old,
              transactions: [],
              pagination: { ...old.pagination, total: 0 },
            };
          },
        );
      } else {
        const unstageIds = new Set(input);

        // Remove from list cache
        queryClient.setQueriesData<TransactionsPageData>(
          {
            queryKey: orpc.shipAndDebit.e844sdTransactions.list.key(),
          },
          (old) => {
            if (!old) return old;
            const filtered = old.transactions.filter(
              (t) => !unstageIds.has(t.id),
            );
            return {
              ...old,
              transactions: filtered,
              pagination: {
                ...old.pagination,
                total:
                  old.pagination.total -
                  (old.transactions.length - filtered.length),
              },
            };
          },
        );
      }

      return { previousList };
    },

    onError: (error: Error, _variables, context) => {
      // Restore snapshots on failure
      if (context?.previousList) {
        for (const [key, data] of context.previousList) {
          queryClient.setQueryData(key, data);
        }
      }

      showToast({
        message: error.message || 'Failed to unstage transactions',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },

    onSettled: (_data, _error, variables) => {
      if (Array.isArray(variables)) {
        invalidateE844sdQueriesExceptList(queryClient);
      } else {
        invalidateE844sdQueries(queryClient);
      }
    },
  });
}

/**
 * Un-post posted transactions (clear posted_at → back to plain `staged`) so
 * they can be edited again. Posting-gated admin action. Rows stay in the
 * staged list (they're still `staged`), so no optimistic removal — just
 * invalidate so the posted flag / row actions refresh.
 */
export function useUnpostTransactions() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (transactionIds: string[]) =>
      rpcClient.shipAndDebit.e844sdTransactions.unpost({ transactionIds }),

    onSuccess: (data) => {
      // The backend only un-posts rows that are still staged + posted, so the
      // actual count can be lower than the number of ids sent (e.g. a bulk
      // selection that also included non-posted rows). Report what happened.
      showToast({
        message: `Un-posted ${data.unpostedCount} transaction(s)`,
        title: 'Success',
        color: 'success',
        icon: 'check',
      });
    },

    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to un-post transactions',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },

    onSettled: () => {
      invalidateE844sdQueries(queryClient);
    },
  });
}

/**
 * Hook for updating a staged transaction's editable fields
 */
export function useUpdateStagedTransaction() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: {
      transactionId: string;
      updates: Record<string, unknown>;
    }) => {
      return rpcClient.shipAndDebit.e844sdTransactions.updateStaged(input);
    },
    onSuccess: (data, variables) => {
      patchTransactionInListCache({
        queryClient,
        transactionId: variables.transactionId,
        updates: data.transaction,
      });
      invalidateE844sdQueriesExceptList(queryClient);
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to update transaction',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}

/**
 * Hook for bulk-updating one field across many transactions at once.
 * Mirrors useUpdateStagedTransaction but accepts a list of IDs.
 */
export function useBulkUpdateTransactions() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: {
      transactionIds: string[];
      updates: Record<string, unknown>;
      comment?: string;
    }) => {
      return rpcClient.shipAndDebit.e844sdTransactions.bulkUpdate(input);
    },
    onSuccess: (data, variables) => {
      const { updatedCount, skippedCount, recalculatedCount, commentedCount } =
        data;
      const total = variables.transactionIds.length;
      const parts: string[] = [];
      if (updatedCount > 0)
        parts.push(`Updated ${updatedCount} of ${total} transaction(s)`);
      if (skippedCount > 0) parts.push(`${skippedCount} skipped`);
      if (recalculatedCount > 0)
        parts.push(`${recalculatedCount} recalculated`);
      if (commentedCount > 0)
        parts.push(`Comment added to ${commentedCount} transaction(s)`);
      showToast({
        message: parts.join(' · '),
        title: 'Success',
        color: 'success',
        icon: 'check',
      });
      if (Object.keys(variables.updates).length > 0) {
        for (const id of variables.transactionIds) {
          patchTransactionInListCache({
            queryClient,
            transactionId: id,
            updates: variables.updates,
          });
        }
      }
      invalidateE844sdQueriesExceptList(queryClient);
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to bulk update transactions',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}

export function useAddComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { transactionId: string; text: string }) => {
      return rpcClient.shipAndDebit.e844sdTransactions.addComment(input);
    },
    onSuccess: () => {
      invalidateE844sdQueriesExceptList(queryClient);
    },
  });
}

export function useEditComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { activityId: string; text: string }) => {
      return rpcClient.shipAndDebit.e844sdTransactions.editComment(input);
    },
    onSuccess: () => {
      invalidateE844sdQueriesExceptList(queryClient);
    },
  });
}

export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { activityId: string }) => {
      return rpcClient.shipAndDebit.e844sdTransactions.deleteComment(input);
    },
    onSuccess: () => {
      invalidateE844sdQueriesExceptList(queryClient);
    },
  });
}

/**
 * Hook for staging internal transactions (pending → staged_internal)
 */
export function useStageInternalTransactions() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (transactionIds: string[]) => {
      return rpcClient.shipAndDebit.e844sdTransactions.stageInternal({
        transactionIds,
      });
    },
    onSuccess: (_data, variables) => {
      removeTransactionsFromListCache(queryClient, variables);
      invalidateE844sdQueriesExceptList(queryClient);
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to stage internal transactions',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}

/**
 * Hook for unstaging internal transactions (staged_internal → pending)
 */
export function useUnstageInternalTransactions() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: string[] | { all: true }) => {
      const payload = Array.isArray(input) ? { transactionIds: input } : input;
      return rpcClient.shipAndDebit.e844sdTransactions.unstageInternal(payload);
    },
    onSuccess: (_data, variables) => {
      if (Array.isArray(variables)) {
        removeTransactionsFromListCache(queryClient, variables);
        invalidateE844sdQueriesExceptList(queryClient);
      } else {
        invalidateE844sdQueries(queryClient);
      }
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to unstage internal transactions',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}

/**
 * Hook for fetching staged transaction stats (count, total debit, vendor count)
 */
export function useStagedStats() {
  return useQuery(
    orpc.shipAndDebit.e844sdTransactions.getStagedStats.queryOptions({
      input: {},
    }),
  );
}

/**
 * Hook for summing claim_amount across the user's selected staged
 * transaction IDs. Returns `undefined` when the selection is empty so
 * callers can fall back to the page-wide total.
 */
export function useSelectedClaimAmount(transactionIds: string[]) {
  const sortedIds = [...transactionIds].sort();
  return useQuery({
    ...orpc.shipAndDebit.e844sdTransactions.getSelectedClaimAmount.queryOptions(
      {
        input: { transactionIds: sortedIds },
      },
    ),
    enabled: sortedIds.length > 0,
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook for searching all transactions across all statuses
 */
export interface SearchAllTransactionsParams {
  divisions?: string[];
  sellingLocations?: string[];
  companyIds?: string[];
  customer?: string;
  invoiceNo?: string;
  orderLineNo?: string;
  productCode?: string;
  supplierContractNo?: string;
  limit?: number;
  offset?: number;
}

/**
 * Whether the Search Transactions query should run. The query is disabled until
 * at least one search filter is set — but EVERY user-facing filter must be
 * listed here. Omitting one (e.g. productCode) silently disables the query when
 * that filter is the only one set, so the page renders "no results" even though
 * the backend would have matched. Keep this in sync with the page's `canSearch`
 * and the backend `searchAllTransactionsProcedure` conditions.
 */
export function searchAllHasFilters(
  params: SearchAllTransactionsParams,
): boolean {
  return !!(
    (params.divisions && params.divisions.length > 0) ||
    (params.sellingLocations && params.sellingLocations.length > 0) ||
    (params.companyIds && params.companyIds.length > 0) ||
    params.customer ||
    params.invoiceNo ||
    params.orderLineNo ||
    params.productCode ||
    params.supplierContractNo
  );
}

export function useSearchAllTransactions(params: SearchAllTransactionsParams) {
  return useQuery({
    ...orpc.shipAndDebit.e844sdTransactions.searchAll.queryOptions({
      input: params,
    }),
    enabled: searchAllHasFilters(params),
  });
}

/**
 * Hook for fetching user activity log
 */
export function useActivityLog(params: {
  activityTypes?: string[];
  userIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery(
    orpc.shipAndDebit.e844sdTransactions.getActivityLog.queryOptions({
      input: params,
    }),
  );
}

// The export resolves to a streamed file rather than a JSON value, so it runs
// as a browser navigation to the procedure's RPC GET URL instead of through the
// typed client — see `startRpcDownloadInFrame`. Taking the path from the query
// utils keeps it in step with the contract: renaming the procedure breaks this
// line.
const [EXPORT_TRANSACTIONS_PATH] =
  orpc.shipAndDebit.e844sdTransactions.exportExcel.key();

/** The page state an export needs: its filters, and what's in scope. */
export interface ExportTransactionsArgs {
  exportKind: SdExportKind;
  /**
   * The page's current filters. Load-bearing when the scope is the view itself
   * — that's how a select-all is expressed: as a few hundred bytes rather than
   * its 100K+ ids. Ignored by the server when the narrowing resolves to an
   * explicit `includeIds`.
   */
  filters?: Omit<
    ExportTransactionsRequest,
    'exportKind' | 'includeIds' | 'excludeIds'
  >;
  scope: ExportScope;
}

/**
 * Download the selected transactions as a streamed XLSX.
 *
 * The bytes never pass through a JSON response, so neither the server nor the
 * tab buffers the workbook, and the browser handles the transfer as a native
 * download — which is what makes a large export survive a slow connection
 * instead of dying on `fetch`'s ~300s ceiling.
 *
 * Two steps, because a streamed download has to be a navigation and a
 * navigation can only be a GET: the request is POSTed and parked first, and the
 * navigation carries only the id that comes back. That holds the download URL at
 * a constant ~120 characters for 3 rows or 90,000 — no id list goes near it, so
 * there is no selection size at which the export behaves differently.
 */
export function useExportTransactionsExcel() {
  const { showToast } = useToast();
  const gt = useGT();

  return useMutation({
    mutationFn: async ({
      exportKind,
      filters,
      scope,
    }: ExportTransactionsArgs) => {
      const narrowing = await resolveExportNarrowing(scope);
      // An explicitly empty selection asks for zero rows. The server honours
      // that (it would stream a header-only workbook), but there is nothing
      // worth handing the user, so don't start a download at all.
      if (narrowing.includeIds?.length === 0) return { started: false };
      // `includeIds` is scoped by id and status ALONE — the server ignores every
      // filter field beside it (see ExportTransactionsRequestSchema), so sending
      // them would describe a view the export will not consult.
      const { selectionId } =
        await rpcClient.shipAndDebit.e844sdTransactions.createExportSelection({
          exportKind,
          ...(narrowing.includeIds ? {} : filters),
          ...narrowing,
        });
      // A hidden frame, not `window.open`: the request above has already
      // consumed the click's transient activation, so a pop-up would be blocked.
      await startRpcDownloadInFrame(EXPORT_TRANSACTIONS_PATH, { selectionId });
      return { started: true };
    },
    onSuccess: ({ started }) => {
      if (!started) return;
      showToast({
        message: gt(
          'Your export has started downloading. Large exports can take a few minutes.',
          { context: 'toast when a Ship & Debit transactions export begins' },
        ),
        title: gt('Export started', {
          context: 'toast title when a Ship & Debit export begins',
        }),
        color: 'info',
        icon: 'info',
      });
    },
    onError: (error: Error) => {
      showToast({
        message:
          error.message ||
          gt('Failed to export to Excel', {
            context: 'error toast when a Ship & Debit export fails',
          }),
        title: gt('Error', { context: 'error toast title' }),
        color: 'error',
        icon: 'error',
      });
    },
  });
}

export function useDownloadPostingExcel(options?: {
  onWarnings?: (warnings: { divisionKey: string; message: string }[]) => void;
}) {
  const { showToast } = useToast();

  return useMutation(
    orpc.shipAndDebit.e844sdTransactions.downloadPostingExcel.mutationOptions({
      onSuccess: (data) => {
        // Hand warnings to the caller (the staged page renders them in its
        // own persistent banner). If a handler is present it OWNS the warning
        // display, so we must not also toast them — that double-surfaces the
        // same message.
        options?.onWarnings?.(data.warnings);
        if (data.files.length > 0) {
          for (const file of data.files) {
            triggerExcelDownload(file.excel, file.filename);
          }
          return;
        }
        // No files: no eligible transactions, or every matched row was skipped
        // for a missing GL account / blank location (surfaced in `warnings`).
        // This is a no-data outcome, not an error. When the caller has a
        // warnings banner AND there are warnings, let the banner show them.
        // Otherwise toast: the specific reason(s) when present (persistent so
        // the user can read/copy them), else a plain "nothing to post" note.
        if (options?.onWarnings && data.warnings.length > 0) return;
        const hasWarnings = data.warnings.length > 0;
        const message = hasWarnings
          ? data.warnings.map((w) => w.message).join(' ')
          : 'No staged transactions matched the selected filters, so there is nothing to post.';
        showToast({
          message,
          title: 'No transactions to post',
          color: 'warning',
          persistent: hasWarnings,
          ...(hasWarnings && { copyText: message }),
        });
      },
      onError: (error: Error) => {
        showToast({
          message: error.message || 'Failed to generate GL posting Excel',
          title: 'Error',
          color: 'error',
          icon: 'error',
        });
      },
    }),
  );
}

export function useClassificationStats(params: {
  startDate: string;
  endDate: string;
  granularity?: 'day' | 'week' | 'month';
}) {
  return useQuery({
    ...orpc.shipAndDebit.e844sdTransactions.getClassificationStats.queryOptions(
      {
        input: params,
      },
    ),
    placeholderData: keepPreviousData,
  });
}

export function useStatusCounts() {
  return useQuery(
    orpc.shipAndDebit.e844sdTransactions.getStatusCounts.queryOptions({
      input: {},
    }),
  );
}

export function useClassificationBreakdown(params: {
  startDate: string;
  endDate: string;
}) {
  return useQuery({
    ...orpc.shipAndDebit.e844sdTransactions.getClassificationBreakdown.queryOptions(
      {
        input: params,
      },
    ),
    placeholderData: keepPreviousData,
  });
}

export function useUserActivityStats(params: {
  startDate: string;
  endDate: string;
  groupByUser?: boolean;
}) {
  return useQuery({
    ...orpc.shipAndDebit.e844sdTransactions.getUserActivityStats.queryOptions({
      input: params,
    }),
    placeholderData: keepPreviousData,
  });
}

export function useSupplierAcceptanceRates(params?: {
  deliveryMethod?: 'edi' | 'email';
  startDate?: string;
  endDate?: string;
}) {
  return useQuery({
    ...orpc.shipAndDebit.e844sdTransactions.getSupplierAcceptanceRates.queryOptions(
      {
        input: {
          deliveryMethod: params?.deliveryMethod,
          startDate: params?.startDate,
          endDate: params?.endDate,
        },
      },
    ),
    placeholderData: keepPreviousData,
  });
}
